import {
  Alert,
  Button,
  Flex,
  Heading,
  IconAddLine,
  View,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { ProgramTypeService } from "../../services/ProgramTypeService";
import { sort } from "../../utils/utils";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import ProgramTypeCard from "./ProgramTypeCard";
import ProgramTypesModal from "./ProgramTypesModal";

const ProgramTypes = () => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [programTypes, setProgramTypes] = useState(null);

  useEffect(() => {
    ProgramTypeService.getProgramTypes()
      .then((types) => setProgramTypes(types))
      .catch(() =>
        showNotification(
          t("notifications.general.fetchError", { obj: "Program Type" }),
          ERROR
        )
      );
  }, []);

  return (
    <View as="div">
      <View as="div" borderWidth="none none small none" margin="0 0 small 0">
        <Flex justifyItems="space-between" alignItems="end">
          <Flex.Item>
            <Heading as="h2" level="h3">
              {t("setup.programTypes")}
            </Heading>
          </Flex.Item>
          <Flex.Item>
            <AddProgramTypeButton setProgramTypes={setProgramTypes} />
          </Flex.Item>
        </Flex>
      </View>
      {programTypes ? (
        programTypes.length > 0 ? (
          <Flex wrap="wrap">
            {sort(programTypes, (type) => type.name).map((type, index) => (
              <Flex.Item
                key={type.id}
                size="50%"
                padding={
                  index % 2 === 0
                    ? "0 x-small x-small 0"
                    : "0 0 x-small x-small"
                }
              >
                <ProgramTypeCard
                  programType={type}
                  setProgramTypes={setProgramTypes}
                />
              </Flex.Item>
            ))}
          </Flex>
        ) : (
          <Alert>{t("programType.noProgramTypes")}</Alert>
        )
      ) : (
        <Skeleton count={3} style={{ marginBottom: 10, height: 60 }} />
      )}
    </View>
  );
};

const AddProgramTypeButton = ({ setProgramTypes }) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [open, setOpen] = useState(false);

  const handleCreateProgramType = async (typeName) => {
    try {
      const newProgramType = await ProgramTypeService.createProgramType({
        name: typeName,
      });
      setProgramTypes((prev) => [...prev, newProgramType]);
      setOpen(false);
      showNotification(
        t("notifications.general.createSuccess", { obj: "Program Type" })
      );
    } catch {
      showNotification(
        t("notifications.general.createError", { obj: "Program Type" }),
        ERROR
      );
    }
  };

  return (
    <>
      <Button
        id="add-program-type-button"
        color="primary"
        renderIcon={IconAddLine}
        onClick={() => setOpen(true)}
      >
        {t("programType.addProgramType")}
      </Button>
      <ProgramTypesModal
        open={open}
        setOpen={setOpen}
        handleSubmit={handleCreateProgramType}
      />
    </>
  );
};

export default ProgramTypes;
